/**
 * @file Weapon debug readout.
 *
 * A pure formatter over a {@link ../components/Weapon.js Weapon}: everything a
 * weapon bug needs — state, owner, ammo, cadence, reload, the direction it last
 * fired in and what that shot hit — as plain text. It touches no DOM and no
 * world, so it is equally usable from the HUD panel
 * (`config.debug.showWeapon`), a console call, or a test.
 */

/**
 * @param {number} value Number to print.
 * @param {number} [digits] Decimal places.
 * @returns {string}
 */
function round(value, digits = 2) {
  if (!Number.isFinite(value)) return String(value);
  return value.toFixed(digits);
}

/**
 * Formats a weapon's full internal state.
 *
 * @param {import('../components/Weapon.js').Weapon} weapon Weapon to inspect.
 * @returns {string} Multi-line report, empty when there is no weapon.
 */
export function describeWeapon(weapon) {
  if (!weapon) return '';

  const owner = weapon.owner ? `${weapon.owner.name || 'entity'}#${weapon.owner.id}` : 'none';
  const ammo = weapon.infiniteAmmo
    ? 'infinite'
    : `${weapon.currentAmmo}/${weapon.magazineCapacity} (+${weapon.reserveAmmo})`;
  const direction = weapon.lastShotDirection;
  const hit = weapon.lastHitTarget
    ? `${weapon.lastHitTarget.name || 'entity'}#${weapon.lastHitTarget.id} ` +
      `at (${round(weapon.lastHitPosition.x)}, ${round(weapon.lastHitPosition.z)}) ` +
      `for ${round(weapon.lastDamageDealt, 1)}`
    : 'none';

  return [
    `${weapon.name} [${weapon.key || 'custom'}]`,
    `state    ${weapon.state}${weapon.enabled ? '' : ' (disabled)'}`,
    `owner    ${owner}`,
    `ammo     ${ammo}`,
    `shot     ${weapon.shotType} · ${weapon.fireMode} · ${round(weapon.fireRate, 1)}/s`,
    `cooldown ${round(weapon.getRemainingShotCooldown())}s`,
    `reload   ${weapon.isReloading() ? `${Math.round(weapon.getReloadProgress() * 100)}%` : 'idle'}`,
    `muzzle   (${round(weapon.muzzle.x)}, ${round(weapon.muzzle.z)})`,
    `aim      (${round(direction.x)}, ${round(direction.z)}) · spread ${round(weapon.spreadDegrees, 1)}°`,
    `hit      ${hit}`,
    `result   ${weapon.lastFireResult ?? 'none'}`
  ].join('\n');
}
